import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const ROLE_OPTIONS = [
  { value: 'technician', label: 'Technician', description: 'Field operations and service delivery' },
  { value: 'manager', label: 'Manager', description: 'Operations and team management' },
  { value: 'salesperson', label: 'Salesperson', description: 'Sales and customer management' },
  { value: 'admin', label: 'Admin', description: 'Full access except billing' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
];

const InviteUserModal = ({ onClose, onSuccess }) => {
  const { user, orgMembership } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'technician',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  const validate = () => {
    const errs = {};
    if (!formData?.email?.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) errs.email = 'Enter a valid email address';
    if (!formData?.name?.trim()) errs.name = 'Name is required';
    if (!formData?.role) errs.role = 'Role is required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSendInvite = async () => {
    if (!validate()) return;
    setLoading(true);
    setError(null);
    try {
      const orgName = orgMembership?.organizations?.name || 'FieldServi';
      const inviterName = user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Your Administrator';
      const inviteLink = `${window.location?.origin}/login`;

      // Save invitation record
      const { error: dbErr } = await supabase?.from('invitations')?.insert({
        organization_id: orgMembership?.organization_id,
        email: formData?.email,
        name: formData?.name,
        role: formData?.role,
        message: formData?.message || null,
        invited_by: user?.id,
        invited_by_name: inviterName,
        status: 'pending',
      });
      if (dbErr) console.warn('Could not save invitation record:', dbErr?.message);

      // Send invitation email via Resend edge function
      const { data: fnData, error: fnErr } = await supabase?.functions?.invoke('send-invitation-email', {
        body: {
          recipientEmail: formData?.email,
          recipientName: formData?.name,
          inviterName,
          orgName,
          inviteLink,
          role: formData?.role,
          message: formData?.message,
        },
      });

      if (fnErr) throw new Error(fnErr.message || 'Failed to send invitation email');
      if (fnData?.error) throw new Error(fnData.error);

      setSentTo(formData?.email);
      setStep(2);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedRole = ROLE_OPTIONS?.find(r => r?.value === formData?.role);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1500] flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-elevation-5 w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">Invite Team Member</h2>
              <p className="text-xs text-muted-foreground">Send an invitation email via Resend</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-5">
          {step === 1 ? (
            <div className="space-y-4">
              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">1</div>
                  <span className="text-xs font-medium text-foreground">Details</span>
                </div>
                <div className="flex-1 h-px bg-border"></div>
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-bold">2</div>
                  <span className="text-xs text-muted-foreground">Sent</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                  <Input
                    type="text"
                    placeholder="Jane Smith"
                    value={formData?.name}
                    onChange={e => setFormData(p => ({ ...p, name: e?.target?.value }))}
                    className={errors?.name ? 'border-red-400' : ''}
                  />
                  {errors?.name && <p className="text-xs text-red-500 mt-1">{errors?.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email Address *</label>
                  <Input
                    type="email"
                    placeholder="jane@company.com"
                    value={formData?.email}
                    onChange={e => setFormData(p => ({ ...p, email: e?.target?.value }))}
                    className={errors?.email ? 'border-red-400' : ''}
                  />
                  {errors?.email && <p className="text-xs text-red-500 mt-1">{errors?.email}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Role *</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ROLE_OPTIONS?.map(role => (
                    <button
                      key={role?.value}
                      type="button"
                      onClick={() => setFormData(p => ({ ...p, role: role?.value }))}
                      className={`p-3 rounded-xl border text-left transition-smooth ${
                        formData?.role === role?.value
                          ? 'border-primary bg-primary/8 text-primary' :'border-border hover:border-primary/40 hover:bg-muted/30'
                      }`}
                    >
                      <p className="text-sm font-semibold">{role?.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{role?.description}</p>
                    </button>
                  ))}
                </div>
                {errors?.role && <p className="text-xs text-red-500 mt-1">{errors?.role}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Personal Message <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  placeholder="Add a personal note to the invitation..."
                  value={formData?.message}
                  onChange={e => setFormData(p => ({ ...p, message: e?.target?.value }))}
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-foreground placeholder:text-muted-foreground"
                />
              </div>

              {/* Preview */}
              {formData?.email && formData?.name && (
                <div className="bg-muted/40 rounded-xl p-4 border border-border">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Invitation Preview</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{formData?.name}</p>
                      <p className="text-xs text-muted-foreground">{formData?.email}</p>
                    </div>
                    <div className="ml-auto">
                      <span className="px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold capitalize">
                        {selectedRole?.label}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="AlertCircle" size={14} color="#ef4444" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" fullWidth onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleSendInvite}
                  loading={loading}
                  disabled={loading}
                  iconName="Send"
                  iconPosition="left"
                >
                  Send Invitation
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} color="#10b981" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Invitation Sent!</h3>
              <p className="text-sm text-muted-foreground mb-1">
                An invitation email has been sent to
              </p>
              <p className="text-sm font-semibold text-foreground mb-4">{sentTo}</p>
              <div className="bg-muted/40 rounded-xl p-4 text-left space-y-2 mb-6">
                {[
                  'Invitation email delivered via Resend',
                  `${formData?.name} will receive a link to set up their account`,
                  `They will be assigned the ${selectedRole?.label} role`,
                  'Invitation expires in 7 days',
                ]?.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={13} color="#10b981" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" fullWidth onClick={() => { setStep(1); setFormData({ email: '', name: '', role: 'technician', message: '' }); setSentTo(null); }}>
                  Send Another
                </Button>
                <Button variant="default" fullWidth onClick={onClose}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
